/**
 * Account-scoped filesystem root — the FS half of the namespacing that
 * services/storageNamespace.js provides for localStorage.
 *
 * Rust owns the root and hands it over via `account_data_dir()`:
 *
 *   <AppLocalData>/users/<accountId>/    signed in
 *   <AppLocalData>/users/_local/         signed out
 *
 * Everything JS writes per-account goes under it — request/response bodies
 * (temp/history/responses), the open request tabs and their response payloads
 * (temp/workspace-tabs/) and the YAML workspace mirrors (workspace-mirror/) —
 * so account B's session builds paths that simply do not lead to account A's
 * files. Same invariant as the localStorage keys: unreachable by construction,
 * not by filtering.
 *
 * NOTE: `local-workspaces/ns-<account>/` deliberately stays OUTSIDE this root.
 * It holds the local workspace documents, which exist nowhere else, and the
 * account root is purged on sign-out. The FILES inside the root are
 * re-derivable (mirrors are copies of a snapshot; history bodies are debug
 * scratch). Its DATABASE is not all re-derivable since cloud/local items
 * (15.09): boards/documents "On this device" and changes not yet synced live
 * there — Sign out asks before removing them and can keep them
 * (`purgeAccountData(..., { keepLocalOnly })`, sync/signout.rs).
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import {
  getStorageNamespace,
  readPurgeMarker,
  writePurgeMarker,
  mergeLocalWorkspaceManifests,
  LEGACY_FS_PURGE_KEY,
  LOCAL_NAMESPACE,
} from './storageNamespace';

/** Cached per namespace — invalidated whenever the signed-in account changes. */
let cached = null;

/**
 * Absolute account root, or null outside Tauri (web/dev builds have no FS).
 * @returns {Promise<string|null>}
 */
export async function accountDataDir() {
  if (!isTauri()) return null;
  const ns = getStorageNamespace();
  if (cached?.ns !== ns) {
    const promise = Promise.resolve()
      .then(() => invoke('account_data_dir'))
      .catch((err) => {
        if (cached?.ns === ns) cached = null;
        throw err;
      });
    cached = { ns, promise };
  }
  try {
    const dir = await cached.promise;
    return dir ? String(dir).replace(/[/\\]+$/, '') : null;
  } catch (err) {
    console.warn('[accountData] account_data_dir unavailable:', err);
    return null;
  }
}

/**
 * Absolute path to `relative` inside the current account's root, or null when
 * there is no root (non-Tauri). Callers pass this straight to plugin-fs
 * WITHOUT a baseDir — the path is already absolute.
 */
export async function accountPath(relative) {
  const root = await accountDataDir();
  if (!root) return null;
  const clean = String(relative ?? '').replace(/\\/g, '/').replace(/^\/+/, '');
  return clean ? `${root}/${clean}` : root;
}

/**
 * What an explicit sign-out of `userId` would delete that exists nowhere else:
 * `{ deviceBoards, deviceDocuments, deviceOther, unsyncedChanges }`.
 *
 * `null` means "could not tell" (web build, no id, the open database belongs to
 * another account, a command error). Callers must treat that as "maybe", never
 * as "nothing to lose".
 *
 * @param {string|number} userId
 * @returns {Promise<{deviceBoards:number, deviceDocuments:number, deviceOther:number, unsyncedChanges:number}|null>}
 */
export async function localOnlySummary(userId) {
  if (!isTauri()) return null;
  if (userId == null || String(userId) === '') return null;
  try {
    const summary = await invoke('account_local_only_summary', { userId: String(userId) });
    return summary && typeof summary === 'object' ? summary : null;
  } catch (err) {
    console.warn('[accountData] account_local_only_summary failed:', err);
    return null;
  }
}

/** Test/lifecycle hook: forget the cached root (namespace change, purge). */
export function resetAccountDataDirCache() {
  cached = null;
}

/**
 * Delete an account's on-disk root (`.../users/<id>/`) — **except its vault**.
 * The teardown primitive for sign-out.
 *
 * It was a plain `rm -rf` until 19.08.2026, which erased the vault along with
 * everything else. Harmless-looking for a synced account (cold start pulls it
 * back), permanent for one that has never synced — and since Free became
 * local-only (16.08.2026) a free user's vault syncs nowhere, so signing out
 * destroyed every password they had, silently, with no copy anywhere. Rust
 * (`sync::purge_account_root`) now keeps `apiboo.db`'s `vault_*` tables and
 * `vault.snapshot.json` and clears the rest, so this call is no longer a
 * complete erase — see that function for what survives and why.
 *
 * The id is REQUIRED. It used to be optional — an omitted id sent `{}` and
 * Rust deleted "whichever account is open", which resolves to the signed-out
 * `_local` bucket by default. A caller that does not know which account it is
 * tearing down must delete NOTHING: `_local` holds the signed-out sync
 * database (task-manager boards, extension documents), the signed-out vault
 * snapshot and client certificates, none of which is mirrored anywhere.
 * Leaving one account root behind is recoverable on the next sign-out;
 * deleting the wrong one is not.
 *
 * Rust rejects a missing id too (`sync::purge_target_id`) — this is the near
 * side of the same rule, so the no-op is silent-and-logged rather than a
 * thrown command error on a teardown path.
 *
 * `keepLocalOnly` (24.09.2026) is the sign-out dialog's "Keep on this device"
 * answer: boards and documents stored only on this device, and changes the
 * server has not received yet, survive in this account's own root; everything
 * the server has still goes (`sync/signout.rs`).
 *
 * `keepMirrorIds` (04.10.2026, with `keepLocalOnly` only): workspaces whose git
 * history exists only on this device. Their `workspace-mirror/<id>` folder —
 * every saved version — survives; Rust refuses the whole purge if an id is not
 * one plain folder name (`sync/signout_mirrors.rs`). Ignored by the full purge.
 *
 * `keepScopeMirrors` (T10, with `keepLocalOnly` only): the `scope-mirror/<folder>`
 * names of scopes tracked on this device only ("personal") — kept the same way.
 *
 * @param {string|number} userId - the OUTGOING account. Required.
 * @param {string} [reason]
 * `removeLocalHistories` (05.10.2026): without it Rust keeps every git
 * repository in the account's mirror folders, decided from disk.
 *
 * @param {{ keepLocalOnly?: boolean, keepMirrorIds?: string[], keepScopeMirrors?: string[], removeLocalHistories?: boolean }} [options]
 * @returns {Promise<boolean>} true when a root was actually purged.
 */
export async function purgeAccountData(
  userId,
  reason = 'unspecified',
  { keepLocalOnly = false, keepMirrorIds = [], keepScopeMirrors = [], removeLocalHistories = false } = {}
) {
  if (!isTauri()) return false;
  const accountId = userId == null || String(userId) === '' ? null : String(userId);
  if (accountId == null) {
    console.warn(
      '[accountData] purgeAccountData called without an account id — refusing to purge whichever account is open'
    );
    return false;
  }
  try {
    const keep = keepLocalOnly === true;
    const mirrorIds = keep && Array.isArray(keepMirrorIds) ? keepMirrorIds.map(String) : [];
    const scopeFolders = keep && Array.isArray(keepScopeMirrors) ? keepScopeMirrors.map(String) : [];
    await invoke('account_data_purge', {
      userId: accountId,
      reason: String(reason),
      keepLocalOnly: keep,
      ...(mirrorIds.length > 0 ? { keepMirrorIds: mirrorIds } : {}),
      ...(scopeFolders.length > 0 ? { keepScopeMirrors: scopeFolders } : {}),
      // Git repositories on this device are kept by Rust unless this is true:
      // only "Remove and sign out" and Delete account say so (05.10.2026).
      ...(removeLocalHistories === true ? { removeLocalHistories: true } : {}),
    });
    resetAccountDataDirCache();
    return true;
  } catch (err) {
    console.warn('[accountData] account_data_purge failed:', err);
    return false;
  }
}

/**
 * Pre-namespace, device-global trees written by older builds. Deleted once, on
 * first run under the new layout — never migrated: an un-namespaced tree has
 * no owner, and everything in these three is RE-DERIVABLE (mirrors are copies
 * of a snapshot, history and tab bodies are caches), so handing them to
 * whoever happens to launch the app next buys nothing and leaks one account's
 * bearer tokens to the next.
 */
const LEGACY_TREES = ['temp/history/responses', 'workspace-mirror', 'temp/workspace-tabs'];

/**
 * `local-workspaces/` is the exception, and it is not in the list above: its
 * pre-namespace `<id>/` children are local workspace DOCUMENTS that exist on
 * no server and in no other directory. They are MOVED into
 * `local-workspaces/ns-_local/`, never deleted.
 *
 * `_local` — the signed-out namespace — is the right destination precisely
 * because it does not answer the ownership question either: it is the bucket
 * whose contents `adoptLocalNamespaceWorkspaces()` OFFERS to whoever signs in,
 * re-keyed, leaving the original in place for a second account to claim too.
 * The previous behaviour answered the same question by deleting the workspace,
 * which is the one answer that cannot be revised later.
 */
const LEGACY_WS_ROOT = 'local-workspaces';
const NS_PREFIX = 'ns-';
const LEGACY_WS_TARGET = `${LEGACY_WS_ROOT}/${NS_PREFIX}${LOCAL_NAMESPACE}`;
const WS_MANIFEST = 'manifest.json';

/** Read a file, or null when it is not there / not readable. */
async function readTextOrNull(fs, path, base) {
  try {
    return await fs.readTextFile(path, base);
  } catch {
    return null;
  }
}

/**
 * Merge the pre-namespace manifest into `ns-_local/manifest.json`. The
 * namespaced file is never clobbered: entries are merged, and on an id
 * collision the namespaced entry wins (see mergeLocalWorkspaceManifests).
 *
 * @returns {Promise<boolean>} false ⇒ retry on the next launch; the legacy
 *   file is left exactly as it was.
 */
async function mergeLegacyManifest(fs, base, from, to) {
  const legacyRaw = await readTextOrNull(fs, from, base);
  if (legacyRaw == null) return true; // absent or unreadable: nothing to carry

  let legacyList = [];
  try {
    const parsed = JSON.parse(legacyRaw);
    if (Array.isArray(parsed)) legacyList = parsed;
  } catch {
    legacyList = []; // corrupt: the snapshot dirs are the documents, not this
  }

  let nsList = [];
  let nsPresent = false;
  try {
    nsPresent = await fs.exists(to, base);
  } catch {
    nsPresent = false;
  }
  if (nsPresent) {
    const nsRaw = await readTextOrNull(fs, to, base);
    if (nsRaw == null) {
      console.warn(`[accountData] ${to} exists but is unreadable — not overwriting it`);
      return false;
    }
    try {
      const parsed = JSON.parse(nsRaw);
      if (!Array.isArray(parsed)) throw new Error('not a manifest');
      nsList = parsed;
    } catch {
      console.warn(`[accountData] ${to} is not a manifest array — not overwriting it`);
      return false;
    }
  }

  const payload = JSON.stringify(mergeLocalWorkspaceManifests(nsList, legacyList));
  try {
    await fs.writeTextFile(to, payload, base);
  } catch (err) {
    console.warn(`[accountData] could not write ${to}:`, err);
    return false;
  }
  // Confirm the bytes landed BEFORE dropping the source.
  if ((await readTextOrNull(fs, to, base)) !== payload) {
    console.warn(`[accountData] ${to} did not take the merged manifest — keeping ${from}`);
    return false;
  }
  try {
    await fs.remove(from, base);
  } catch {
    // A leftover legacy manifest is inert: nothing reads it any more.
  }
  return true;
}

/**
 * Move one pre-namespace child of `local-workspaces/` into `ns-_local/`.
 * `rename` is a single atomic step on purpose — a copy loop could leave half a
 * workspace behind, and there is no second copy of these files anywhere.
 */
async function moveLegacyLocalWorkspace(fs, base, from, to, name) {
  try {
    if (await fs.exists(to, base)) {
      // Same id already namespaced. The namespaced copy is the live one; the
      // legacy tree is LEFT WHERE IT IS rather than merged or deleted, so the
      // bytes remain on disk and this is over — retrying could not improve it.
      console.warn(
        `[accountData] ${to} already exists — leaving the pre-namespace ${from} in place rather than overwriting or deleting it`
      );
      return true;
    }
  } catch {
    // `exists` unavailable: fall through and let `rename` decide.
  }
  try {
    await fs.rename(from, to, {
      oldPathBaseDir: base.baseDir,
      newPathBaseDir: base.baseDir,
    });
    return true;
  } catch (err) {
    console.warn(`[accountData] could not move legacy local workspace ${name}:`, err);
    return false;
  }
}

/**
 * One-shot rescue of the pre-namespace `local-workspaces/<id>/` trees.
 * @returns {Promise<boolean>} false ⇒ something is still where it was; the
 *   caller must NOT record the migration as done.
 */
async function migrateLegacyLocalWorkspaces(fs, base) {
  let entries;
  try {
    entries = await fs.readDir(LEGACY_WS_ROOT, base);
  } catch {
    return true; // no such root on this device
  }
  const legacy = (entries || []).filter((e) => e?.name && !e.name.startsWith(NS_PREFIX));
  if (legacy.length === 0) return true;

  try {
    await fs.mkdir(LEGACY_WS_TARGET, { ...base, recursive: true });
  } catch (err) {
    console.warn(`[accountData] could not create ${LEGACY_WS_TARGET}:`, err);
    return false;
  }

  let complete = true;
  for (const entry of legacy) {
    const name = entry.name;
    const from = `${LEGACY_WS_ROOT}/${name}`;
    const to = `${LEGACY_WS_TARGET}/${name}`;
    const ok =
      name === WS_MANIFEST
        ? await mergeLegacyManifest(fs, base, from, to)
        : await moveLegacyLocalWorkspace(fs, base, from, to, name);
    if (!ok) complete = false;
  }
  return complete;
}

export async function purgeLegacyDeviceTrees() {
  if (readPurgeMarker(LEGACY_FS_PURGE_KEY)) return false;

  /**
   * `true` when the path is gone afterwards, `false` when the removal really
   * failed. `remove` throws for "already absent" too, so absence is confirmed
   * with `exists` rather than inferred from the throw — otherwise a genuine
   * permission/IO failure is indistinguishable from a no-op, which is what let
   * the one-shot marker below be written after removing nothing at all.
   */
  const removeIfPresent = async (fs, path, base) => {
    try {
      await fs.remove(path, { ...base, recursive: true });
      return true;
    } catch (err) {
      try {
        if (!(await fs.exists(path, base))) return true; // never there
      } catch {
        // `exists` itself is unavailable/failing — treat as unproven
      }
      console.warn(`[accountData] could not remove legacy tree ${path}:`, err);
      return false;
    }
  };

  let complete = true;
  try {
    const fs = await import('@tauri-apps/plugin-fs');
    const base = { baseDir: fs.BaseDirectory.AppLocalData };

    // MIGRATE FIRST. Nothing under `local-workspaces/` is deleted any more —
    // the prune that used to live here removed exactly the documents this
    // function has no copy of. A `rename` that fails leaves the original
    // untouched and the marker unwritten, so the only two outcomes are "moved"
    // and "still exactly where it was".
    if (!(await migrateLegacyLocalWorkspaces(fs, base))) complete = false;

    for (const tree of LEGACY_TREES) {
      if (!(await removeIfPresent(fs, tree, base))) complete = false;
    }
  } catch (err) {
    console.warn('[accountData] legacy device tree purge failed:', err);
    return false;
  }

  // The marker is a ONE-SHOT: once written the purge never runs again on this
  // device. Writing it after a failed removal would permanently strand
  // whatever is left — including `workspace-mirror`, which has no belt-and-
  // braces backup on the Rust side (it is absent from
  // `sync/mod.rs::STALE_GLOBAL_DIRS`, deliberately: that constant covers only
  // the trees Rust itself once wrote). Leave it unmarked and retry next launch.
  if (!complete) return false;
  writePurgeMarker(LEGACY_FS_PURGE_KEY);
  return true;
}
